import { POLLING_TYPES } from './types';
import { pollExtraction, pollRender, pollProject } from './poller';

// Track in-flight requests to prevent duplicate polling
const inFlightRequests = new Map();

/**
 * Function to perform a single polling cycle for all registered items
 * @param {Map} pollingItems Current polling items
 * @param {Map} statuses Current statuses
 * @param {Function} setStatuses Function to update statuses
 * @param {Function} setPollingItems Function to update polling items
 */
export const pollCycle = async (pollingItems, statuses, setStatuses, setPollingItems) => {
  if (pollingItems.size === 0) return; // Nothing to poll

  console.log(`[PollingContext] Polling ${pollingItems.size} items...`);

  const pollingPromises = [];

  for (const [itemId, { itemType, projectId }] of pollingItems.entries()) {
    // Skip if there's already an in-flight request for this item
    if (inFlightRequests.has(itemId)) {
      console.log(`[PollingContext] Skipping ${itemId} - request already in flight`);
      continue;
    }

    // Mark this request as in-flight
    inFlightRequests.set(itemId, true);

    const pollPromise = (async () => {
      try {
        let result;

        if (itemType === POLLING_TYPES.EXTRACT) {
          result = await pollExtraction(itemId, projectId, statuses);
        } else if (itemType === POLLING_TYPES.RENDER) {
          result = await pollRender(itemId, projectId, statuses);
        } else if (itemType === POLLING_TYPES.PROJECT) {
          result = await pollProject(itemId, statuses);
        }

        // If we have results and they were updated, update the statuses
        if (result && result.updated) {
          setStatuses(prev => {
            const newStatuses = new Map(prev);
            newStatuses.set(itemId, result.status);
            return newStatuses;
          });
        }

        // Stop polling if complete
        if (result && result.isComplete) {
          console.log(`[PollingContext] Item ${itemId} completed with status ${result.finalStatus}`);
          // Use the state setter's callback form to ensure atomicity
          setPollingItems(prev => {
            const newMap = new Map(prev);
            if (newMap.has(itemId)) {
              newMap.delete(itemId);
              console.log(`[PollingContext] Unregistered ${itemId} inside poll cycle.`);
            }
            return newMap;
          });
        }
      } catch (error) {
        console.error(`[PollingContext] Error polling ${itemType} status for ${itemId}:`, error);
        // Optional: Implement retry logic or unregister on persistent errors
      } finally {
        // Clear the in-flight flag when done
        inFlightRequests.delete(itemId);
      }
    })();

    pollingPromises.push(pollPromise);
  }

  // Wait for all polling promises to complete
  await Promise.all(pollingPromises);
}; 